"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight, Facebook, Instagram, Mail } from "lucide-react";

import { SOCIAL_FACEBOOK_URL, SOCIAL_INSTAGRAM_URL } from "@/lib/copy/client-platform-copy";
import { useWebUserGuide, FALLBACK_WEB_USER_GUIDE } from "@/hooks/use-user-guides";

type FooterLink = { label: string; href: string; external?: boolean; dynamicKey?: "web-user-guide" };

const footerLinks: Record<string, FooterLink[]> = {
  "QUICK LINKS": [
    { label: "Browse", href: "/browse" },
    { label: "How it works", href: "/how-it-works" },
    { label: "About", href: "/about-us" },
    { label: "Trust & Safety", href: "/trust-safety" },
    { label: "FAQ", href: "/faq" },
  ],
  SUPPORT: [
    // { label: "Help Centre", href: "/help" },
    { label: "Contact Us", href: "/contact" },
    {
      label: "User Guide",
      href: FALLBACK_WEB_USER_GUIDE,
      external: true,
      dynamicKey: "web-user-guide",
    },
  ],
  LEGAL: [
    { label: "Terms of Use", href: "/terms-and-conditions" },
    { label: "Privacy Policy", href: "/policy-privacy" },
  ],
};

function FooterColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <>
      <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-neutral-900">
        {children}
      </p>
      <span
        className="mt-1.5 mb-4 block h-[3px] w-10 rounded-sm bg-v2-brand"
        aria-hidden
      />
    </>
  );
}

const socialIcons = (
  <div className="mt-6 flex gap-2.5 ">
    <a
      href={SOCIAL_INSTAGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="h-[34px] w-[34px] shrink-0 rounded-full border border-neutral-200 bg-white flex items-center justify-center text-neutral-600 hover:border-v2-brand hover:text-v2-brand transition-colors"
      aria-label="Rent It Forward on Instagram"
    >
      <Instagram size={14} strokeWidth={1.8} />
    </a>
    <a
      href={SOCIAL_FACEBOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="h-[34px] w-[34px] shrink-0 rounded-full border border-neutral-200 bg-white flex items-center justify-center text-neutral-600 hover:border-v2-brand hover:text-v2-brand transition-colors"
      aria-label="Rent It Forward on Facebook"
    >
      <Facebook size={14} strokeWidth={1.8} />
    </a>
  </div>
);

export const Footer: React.FC = () => {
  const { guide: webUserGuide } = useWebUserGuide();

  const resolveHref = (link: FooterLink): string =>
    link.dynamicKey === "web-user-guide" ? webUserGuide.url : link.href;

  return (
  <footer style={{backgroundColor: '#f1f1ef !important'}} className="bg-neutral-50 text-neutral-700 pt-10 sm:pt-12 lg:pt-14 pb-8">
    <div className="max-w-v2 mx-auto px-4 sm:px-6 lg:px-8">
      {/* Responsive grid:
            • <sm     (mobile):  single column, generous gap-y so the columns read as cards.
            • sm / md (tablet):  2-col so QUICK LINKS / SUPPORT / LEGAL / GET THE APP pair up.
            • xl     (desktop):  five-column layout with brand block taking the left rail. */}
      <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 xl:grid-cols-[1.48fr_repeat(4,minmax(0,1fr))] xl:gap-x-0 xl:gap-y-0">
        <div className="flex flex-col items-start sm:col-span-2 xl:col-span-1 xl:min-h-[200px] xl:pr-10">
          <Link
            href="/"
            aria-label="Rent It Forward — back to home"
            className="mb-5 inline-flex items-center"
          >
            <Image
              src="/images/Rent-IT-Forward-Logo.png"
              alt="Rent It Forward"
              width={440}
              height={120}
              className="h-16 w-auto sm:h-[4.5rem] lg:h-20"
              priority
            />
          </Link>
          <p className=" text-[12px] text-neutral-500 leading-relaxed">ACN 694 838 125</p>
          <p className=" text-[12px] text-neutral-500 leading-relaxed mb-4">
            Rent It Forward Pty Ltd
          </p>
          <p className="text-[14px] font-bold text-neutral-900 leading-snug">
            Rent what you need. Share what you own.
          </p>
          <p className="text-[13px] text-neutral-500 leading-relaxed mt-1 max-w-[17rem]">
            Helping communities access more through trusted local sharing.
          </p>
          {socialIcons}
        </div>

        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title} className="xl:border-l xl:border-neutral-200 xl:pl-10">
            <FooterColumnHeading>{title}</FooterColumnHeading>
            <ul className="space-y-2.5">
              {links.map((link) => {
                const href = resolveHref(link);
                const linkClassName =
                  "flex items-center gap-2 font-sans text-[13px] font-medium text-neutral-700 hover:text-v2-brand transition-colors group";
                const linkContent = (
                  <>
                    <ChevronRight
                      size={10}
                      strokeWidth={2.5}
                      className="text-v2-brand shrink-0 group-hover:translate-x-0.5 transition-transform"
                    />
                    {link.label}
                  </>
                );
                return (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                      >
                        {linkContent}
                      </a>
                    ) : (
                      <Link href={href} className={linkClassName}>
                        {linkContent}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Get the App */}
        <div className="xl:border-l xl:border-neutral-200 xl:pl-10">
          <FooterColumnHeading>GET THE APP</FooterColumnHeading>
          <div className="flex flex-col gap-3">
            {/* Both store badges share an explicit width + height so they read as a matched
                pair. Inner span uses w-full + h-full to stretch to those bounds regardless
                of label length difference between "App Store" and "Google Play". */}
            <a
              href="#"
              aria-label="Download on the App Store"
              className="block w-[180px] h-[56px] transition-transform hover:-translate-y-0.5"
            >
              <span className="flex w-full h-full items-center gap-2.5 rounded-[10px] bg-black text-white px-4">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden className="shrink-0">
                  <path d="M17.05 12.04c-.02-2.18 1.78-3.23 1.86-3.28-1.01-1.49-2.59-1.69-3.16-1.71-1.34-.14-2.62.79-3.31.79-.68 0-1.74-.77-2.86-.75-1.47.02-2.83.86-3.59 2.17-1.53 2.66-.39 6.59 1.1 8.75.73 1.06 1.6 2.25 2.73 2.21 1.1-.05 1.51-.71 2.83-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.15.83-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.53zM14.78 5.59c.6-.74 1.01-1.76.9-2.78-.87.04-1.94.58-2.56 1.31-.55.64-1.04 1.69-.91 2.69.98.07 1.97-.49 2.57-1.22z" />
                </svg>
                <span className="leading-none flex flex-col justify-center">
                  <span className="block text-[9px] tracking-wide">Download on the</span>
                  <span className="block text-[15px] font-semibold leading-tight -mt-0.5">App Store</span>
                </span>
              </span>
            </a>
            <a
              href="#"
              aria-label="Get it on Google Play"
              className="block w-[180px] h-[56px] transition-transform hover:-translate-y-0.5"
            >
              <span className="flex w-full h-full items-center gap-2.5 rounded-[10px] bg-black text-white px-4">
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className="shrink-0">
                  <path d="M3.6 2.4c-.4.4-.6.9-.6 1.6v16c0 .7.2 1.2.6 1.6l.1.1L13 12 3.7 2.4z" fill="#34A853" />
                  <path d="M16.2 15.2L13 12l3.2-3.2 4.8 2.7c1.4.8 1.4 2.2 0 3l-4.8 2.7z" fill="#FBBC04" />
                  <path d="M16.2 15.2L13 12 3.7 21.6c.4.4 1.1.5 1.9.1l10.6-6.5z" fill="#EA4335" />
                  <path d="M16.2 8.8L5.6 2.3c-.8-.5-1.5-.4-1.9.1L13 12l3.2-3.2z" fill="#4285F4" />
                </svg>
                <span className="leading-none flex flex-col justify-center">
                  <span className="block text-[9px] tracking-wide">GET IT ON</span>
                  <span className="block text-[15px] font-semibold leading-tight -mt-0.5">Google Play</span>
                </span>
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Illustration — centred row; figures meet a full-width hairline (requirement) */}
      <div className="w-full xl:-mt-1">
        <div className="flex justify-center px-2 sm:px-4 -mb-[3px]">
          <Image
            src="/images/footer-illustration-image1.png"
            alt="The Rent It Forward community"
            width={900}
            height={150}
            className="relative z-10 h-[88px] w-auto max-w-[min(900px,calc(100vw-3rem))] sm:h-[100px] md:h-[110px] xl:h-[126px]"
            priority={false}
          />
        </div>
        <div className="h-px w-full bg-neutral-300 " style={{marginTop: '3px'}} aria-hidden />
      </div>

      {/* Reach us + Australian-owned — aligned to content width edges */}
      <div className="mt-8 mb-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 text-[12px] font-medium">
        <p className="flex items-center gap-2.5 text-neutral-600 justify-center sm:justify-start">
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-v2-brand/15 text-v2-brand"
            aria-hidden
          >
            <Mail size={15} strokeWidth={1.8} />
          </span>
          <span className="text-neutral-700">
            Reach us at:{" "}
            <a
              href="mailto:hello@rentitforward.com.au"
              className="font-semibold text-v2-brand hover:underline"
            >
              hello@rentitforward.com.au
            </a>
          </span>
        </p>
        <p className="inline-flex items-center gap-2.5 justify-center text-neutral-600 sm:justify-end text-center sm:text-left">
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-v2-brand/40 bg-v2-brand/10 text-lg leading-none"
            aria-hidden
          >
            🇦🇺
          </span>
          Proudly Australian Owned and Operated
        </p>
      </div>

      <p className="text-center text-[11px] text-neutral-500 pt-4">
        © 2026 Rent It Forward Pty Ltd. All rights reserved.
      </p>
    </div>
  </footer>
  );
};
